"use client"

import { useState } from "react"
import Link from "next/link"
import { ArrowRight, Check, FileCode2, Sparkles } from "lucide-react"
import { motion } from "motion/react"

import { useSmoothScroll } from "@/hooks/use-smooth-scroll"
import { Badge } from "@/components/ui/badge"
import { Button, buttonVariants } from "@/components/ui/button"
import { ScrollReveal } from "@/components/scroll-reveal"

import { Footer } from "../home/footer"
import { Header } from "../home/header"
import { IDE_GUIDES } from "../snippets/data"

export function GuidesPage() {
  const handleSmoothScroll = useSmoothScroll()
  const [activeIde, setActiveIde] = useState(IDE_GUIDES[0].id)
  const guide =
    IDE_GUIDES.find((item) => item.id === activeIde) ?? IDE_GUIDES[0]

  return (
    <div className="bg-background min-h-screen transition-colors">
      <Header />
      <main>
        <section className="relative flex min-h-svh items-center overflow-hidden border-b py-20 sm:py-24">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
          >
            <div className="bg-primary/10 absolute top-0 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full blur-3xl" />
            <div className="absolute inset-0 bg-[linear-gradient(to_bottom,transparent,transparent_70%,var(--background))]" />
          </div>
          <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
            <ScrollReveal y={14}>
              <Badge
                variant="outline"
                className="border-primary/20 bg-primary/5 text-primary rounded-full px-3.5 py-1.5"
              >
                <Sparkles className="size-3.5" />
                Five editors. One simple flow.
              </Badge>
            </ScrollReveal>
            <ScrollReveal delay={0.06} y={18}>
              <h1 className="mt-6 text-4xl leading-tight font-semibold tracking-[-0.04em] text-balance sm:text-6xl">
                Set up snippets.
                <span className="text-primary block">Start coding faster.</span>
              </h1>
            </ScrollReveal>
            <ScrollReveal delay={0.12} y={18}>
              <p className="text-muted-foreground mx-auto mt-6 max-w-2xl text-base leading-relaxed text-balance sm:text-lg">
                Step-by-step setup for VS Code, Sublime Text, Zed, Atom, and
                Jupyter. Pick your editor and finish in minutes.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={0.18} y={16}>
              <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() => handleSmoothScroll("setup")}
                  className={buttonVariants({
                    variant: "default",
                    size: "lg",
                    className:
                      "w-fit shadow-[0_0_24px_color-mix(in_oklab,var(--primary)_55%,transparent)] transition-[color,background-color,box-shadow,transform] hover:-translate-y-0.5 hover:shadow-[0_0_36px_color-mix(in_oklab,var(--primary)_75%,transparent)]",
                  })}
                >
                  Choose your IDE
                  <ArrowRight />
                </button>
                <Link
                  href="/generate"
                  className={buttonVariants({
                    variant: "outline",
                    size: "lg",
                    className: "w-fit",
                  })}
                >
                  Open generator
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </section>

        <section
          id="setup"
          className="bg-muted/20 w-full scroll-mt-20 border-b py-6 sm:py-10"
        >
          <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
            <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
              <div>
                <ScrollReveal y={10}>
                  <p className="text-primary mb-2 text-xs font-semibold tracking-wider uppercase">
                    IDE guides
                  </p>
                </ScrollReveal>
                <ScrollReveal delay={0.05} y={14}>
                  <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
                    Set up your snippets
                  </h1>
                </ScrollReveal>
                <ScrollReveal delay={0.1} y={14}>
                  <p className="text-muted-foreground mt-2 max-w-2xl text-sm">
                    Pick your editor. Follow four steps. Test your trigger.
                  </p>
                </ScrollReveal>
              </div>
              <ScrollReveal delay={0.15} y={12}>
                <Link
                  href="/generate"
                  className={buttonVariants({ variant: "outline", size: "sm" })}
                >
                  Open generator
                  <ArrowRight />
                </Link>
              </ScrollReveal>
            </div>

            <div className="bg-card overflow-hidden rounded-2xl border">
              <ScrollReveal y={18} className="p-4 sm:p-6">
                <div className="mb-5">
                  <h2 className="text-sm font-semibold">Choose editor</h2>
                  <p className="text-muted-foreground mt-1 text-xs">
                    Instructions update instantly
                  </p>
                </div>

                <div className="bg-muted mb-6 grid grid-cols-2 gap-1 rounded-xl p-1 sm:grid-cols-3 lg:grid-cols-5">
                  {IDE_GUIDES.map((ide) => {
                    const active = ide.id === activeIde
                    return (
                      <motion.div
                        key={ide.id}
                        layout
                        animate={{
                          scale: active ? 1 : 0.98,
                          opacity: active ? 1 : 0.72,
                        }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                      >
                        <Button
                          type="button"
                          variant={active ? "default" : "ghost"}
                          onClick={() => setActiveIde(ide.id)}
                          aria-pressed={active}
                          className="h-9 w-full text-xs"
                        >
                          {ide.name}
                        </Button>
                      </motion.div>
                    )
                  })}
                </div>

                <motion.div
                  key={guide.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.22, ease: "easeOut" }}
                  className="border-input bg-background overflow-hidden rounded-xl border"
                >
                  <div className="flex flex-col gap-4 border-b p-5 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-3">
                      <div className="bg-primary/10 text-primary flex size-10 items-center justify-center rounded-xl">
                        <FileCode2 className="size-5" />
                      </div>
                      <div>
                        <h2 className="font-semibold">{guide.name}</h2>
                        <p className="text-muted-foreground mt-0.5 text-xs">
                          Save as{" "}
                          <code className="text-foreground font-mono">
                            {guide.file}
                          </code>
                        </p>
                      </div>
                    </div>
                    <span className="text-muted-foreground text-xs">
                      4 steps · about 2 minutes
                    </span>
                  </div>

                  <ol className="bg-border grid gap-px md:grid-cols-2">
                    {guide.steps.map((step, index) => (
                      <li
                        key={step}
                        className="bg-background flex min-h-32 gap-4 p-5 sm:p-6"
                      >
                        <span className="bg-primary text-primary-foreground flex size-8 shrink-0 items-center justify-center rounded-lg text-sm font-semibold">
                          {index + 1}
                        </span>
                        <p className="pt-1 text-sm leading-relaxed">{step}</p>
                      </li>
                    ))}
                  </ol>

                  <div className="bg-primary/5 text-muted-foreground flex gap-3 border-t p-4 text-sm">
                    <Check className="text-primary mt-0.5 size-4 shrink-0" />
                    <p>{guide.note}</p>
                  </div>
                </motion.div>
              </ScrollReveal>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  )
}
